<template>
    <!-- ITDEVELOP-2722: ui/Dialog replaces the hand-rolled native <dialog> +
         manual outside-click/Escape/open-state plumbing. max-h/width overrides
         keep this modal's pre-migration footprint (90% / half viewport on md+). -->
    <Dialog :open="showModal" @update:open="(open) => !open && closeModal()">
        <DialogContent
            name="feedback view"
            size="lg"
            class="max-h-[90vh] max-w-none md:w-1/2"
            :aria-describedby="undefined"
        >
            <DialogTitle class="text-2xl">Feedback</DialogTitle>
            <div class="gap-sm flex flex-col">
                <p>
                    We'd love to hear your thoughts! Please share your feedback about our application. Your input is
                    invaluable in helping us improve and provide a better experience for you. Whether it's a
                    suggestion or just general feedback, we appreciate your time and insights. Thank you for helping
                    us make our application better!
                </p>
                <p>If you have any attachments, please feel free to upload them below.</p>
            </div>
            <div class="gap-xs flex flex-col">
                <div class="gap-xs flex">
                    <span class="text-error">*</span>
                    <label for="feedback-input">Your Feedback</label>
                </div>
                <div class="input-wrapper" :class="{ 'input-error': errorPool.feedbackInput }">
                    <textarea
                        id="feedback-input"
                        v-model="formData.feedbackInput"
                        rows="4"
                        placeholder="Type your feedback here..."
                        auto-focus
                    ></textarea>
                </div>
                <p v-if="errorPool.feedbackInput" class="text-error">
                    {{ errorPool.feedbackInput }}
                </p>
            </div>
            <div class="gap-xs flex flex-col">
                <label>Attachments</label>
                <FileUpload
                    v-model:uploaded-files="formData.attachments"
                    @upload="setAttachments"
                    @remove="formData.attachments.splice($event, 1)"
                />
            </div>

            <InputSelect
                v-if="countryOptions && countryOptions.length > 1"
                :value="formData.country"
                label="Country"
                :options="countryOptions"
                placeholder="Select your country"
                :error-message="errorPool.country"
                :required="true"
                :unique-key="1"
                @update="setCountry"
            />
            <DialogFooter>
                <Button @click="handleSubmit" @keydown.enter="handleSubmit">
                    <span v-if="!submitting">Submit Feedback</span>
                    <i v-else class="i-spinner-white animate-spin" />
                </Button>
                <Button variant="secondary" @click="closeModal">Cancel</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '@/components/ui/dialog';
import FileUpload from '@/components/support/file-upload.vue';
import { useSupportStore } from '@/stores/support';
import { useOrganizationStore } from '@/stores/organization';
import InputSelect from '@/components/support/input-select.vue';
import type { Option } from '@/stores/store-interfaces';
import { getDefaultErrorMessage } from '@/lib/http-error';

const supportStore = useSupportStore();
const organizationStore = useOrganizationStore();
const submitting = ref(false);
const errorPool = ref<Record<string, string>>({});
const countryOptions = ref();

const emit = defineEmits(['close']);
const formData = ref({
    feedbackInput: '',
    attachments: [] as File[],
    country: [] as Option[],
});

const setAttachments = (files: File[]) => {
    formData.value.attachments = files;
};

const setCountry = (value: Option[]) => {
    formData.value.country = value;
};
const props = defineProps<{
    showModal: boolean;
}>();

onMounted(async () => {
    await setup();
});

const setup = async () => {
    if (!organizationStore.usersOrganization || !organizationStore.user) {
        return;
    }

    let regions: Array<{ name: string }> = await organizationStore.getLocations();
    if (!regions || regions.length === 0) {
        const orgDataResponse = await organizationStore.getOrganizationById(
            organizationStore.usersOrganization.organization_id.toString(),
        );
        const orgData = orgDataResponse.data;
        if (orgData && orgData.regions) {
            regions = orgData.regions;
        }
    }

    if (!regions || regions.length === 0) {
        organizationStore.setToastMessage('No regions found. Please contact support.', 'warning');
        return;
    }

    countryOptions.value = regions.map((region) => ({
        label: region.name,
        value: region.name,
    }));

    if (countryOptions.value.length === 1) {
        formData.value.country = [
            {
                label: countryOptions.value[0].label,
                value: countryOptions.value[0].value,
            },
        ];
    }
};

const validateForm = () => {
    errorPool.value = {};
    if (!formData.value.feedbackInput.trim()) {
        errorPool.value.feedbackInput = 'Feedback cannot be empty.';
    }
    if (formData.value.country.length === 0) {
        errorPool.value.country = 'Please select a country.';
    }
    return Object.keys(errorPool.value).length === 0;
};

const handleSubmit = async () => {
    if (submitting.value) return; // Prevent multiple submissions
    submitting.value = true;

    // Logic to handle feedback submission
    if (validateForm() === false) {
        submitting.value = false;
        return;
    }

    try {
        const response = await supportStore.createRequest(
            '74', //CP SD id
            '1083', //Feedback Request type id
            {
                summary: `Feedback from ${organizationStore.usersOrganization?.name ?? 'atNorth'}`,
                description: formData.value.feedbackInput,
                customfield_10938: { value: formData.value.country[0].value },
            },
            formData.value.attachments,
        );

        if (response) {
            organizationStore.setToastMessage('Feedback submitted successfully', 'check');
            closeModal();
        }
    } catch (error) {
        organizationStore.setToastMessage(getDefaultErrorMessage(error), 'error');
    } finally {
        submitting.value = false;
    }
};

const closeModal = () => {
    errorPool.value = {};
    formData.value = {
        feedbackInput: '',
        attachments: [],
        country: [],
    };
    formData.value.attachments = [];
    submitting.value = false;
    emit('close');
};

// ui/Dialog owns open/close mechanics; this watch only preserves the old
// behaviour of resetting the form when the parent closes the modal externally.
watch(
    () => props.showModal,
    (open) => {
        if (!open) {
            errorPool.value = {};
            formData.value = {
                feedbackInput: '',
                attachments: [],
                country: [],
            };
        }
    },
);
watch(
    () => organizationStore.usersOrganization,
    (newVal) => {
        if (newVal) {
            setup();
        }
    },
);
watch(
    () => organizationStore.user,
    (newVal) => {
        if (newVal) {
            setup();
        }
    },
);
</script>

